export type ModelKind = 'text' | 'transcript';

export interface RankedModelOption {
  id: string;
  label: string;
  quality: number;
  cost: number;
  speed: number;
  score: number;
  qualityLabel: string;
  costLabel: string;
}

type ModelMeta = {
  quality: number;
  cost: number;
  speed: number;
};

const KNOWN_MODEL_META: Record<string, ModelMeta> = {
  '@cf/meta/llama-3.1-8b-instruct': { quality: 3, cost: 1, speed: 5 },
  '@cf/meta/llama-3.1-70b-instruct': { quality: 5, cost: 5, speed: 2 },
  '@cf/meta/llama-3.3-70b-instruct-fp8-fast': { quality: 5, cost: 3, speed: 4 },
  '@cf/openai/whisper': { quality: 4, cost: 2, speed: 3 },
};

function clampRating(value: number): number {
  return Math.min(5, Math.max(1, Math.round(value)));
}

function inferModelMeta(modelId: string, kind: ModelKind): ModelMeta {
  const known = KNOWN_MODEL_META[modelId];
  if (known) return known;

  if (kind === 'transcript') {
    return { quality: 3, cost: 2, speed: 3 };
  }

  const sizeMatch = modelId.match(/(\d+(?:\.\d+)?)b/i);
  const sizeB = sizeMatch ? Number.parseFloat(sizeMatch[1]) : null;

  if (sizeB === null) {
    return { quality: 3, cost: 3, speed: 3 };
  }

  if (sizeB <= 8) return { quality: 3, cost: 1, speed: 5 };
  if (sizeB <= 14) return { quality: 3, cost: 2, speed: 4 };
  if (sizeB <= 34) return { quality: 4, cost: 3, speed: 3 };
  if (sizeB <= 70) return { quality: 5, cost: 5, speed: 2 };
  return { quality: 5, cost: 5, speed: 1 };
}

function scoreModel(meta: ModelMeta): number {
  return meta.quality * 2 + meta.speed - meta.cost;
}

function qualityLabel(value: number): string {
  if (value >= 5) return 'Highest';
  if (value >= 4) return 'High';
  if (value >= 3) return 'Balanced';
  return 'Basic';
}

function costLabel(value: number): string {
  if (value <= 1) return 'Lowest';
  if (value <= 2) return 'Low';
  if (value <= 3) return 'Medium';
  if (value <= 4) return 'High';
  return 'Highest';
}

function formatModelLabel(modelId: string): string {
  const parts = modelId.split('/');
  const tail = parts[parts.length - 1] ?? modelId;
  return tail
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replace(/\bFp8\b/g, 'FP8');
}

export function rankModels(models: string[], kind: ModelKind): RankedModelOption[] {
  const deduped = Array.from(new Set(models.filter(Boolean)));

  return deduped
    .map((id) => {
      const inferred = inferModelMeta(id, kind);
      const quality = clampRating(inferred.quality);
      const cost = clampRating(inferred.cost);
      const speed = clampRating(inferred.speed);
      const score = scoreModel({ quality, cost, speed });

      return {
        id,
        label: formatModelLabel(id),
        quality,
        cost,
        speed,
        score,
        qualityLabel: qualityLabel(quality),
        costLabel: costLabel(cost),
      };
    })
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (b.quality !== a.quality) return b.quality - a.quality;
      if (a.cost !== b.cost) return a.cost - b.cost;
      return a.label.localeCompare(b.label);
    });
}
